import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home, MapPin, LogOut, User, Menu, X, Users, Activity,
  Award, Key, Phone, Layers, Settings as SettingsIcon, ChevronDown, Map, Music,
  ShoppingBag, Target, Contact, GitCompare, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { AuthProvider, useAuth } from './contexts/JWTAuthContext';
import { ToastProvider } from './components/ToastContainer';
import Dashboard from './components/Dashboard';
import DashboardLegacy from './components/DashboardLegacy';
import Reviews from './components/Reviews';
import Branches from './components/Branches';
import Analysis from './components/Analysis';
import GoogleMaps from './components/GoogleMaps';
import TikTok from './components/TikTok';
import TeamManagement from './components/TeamManagement';
import CronMonitor from './components/CronMonitor';
import NpsLeaderboard from './components/NpsLeaderboard';
import NpsLedger from './components/NpsLedger';
import Calls from './components/Calls';
import CallDetail from './components/CallDetail';
import AgentProfile from './components/AgentProfile';
import Shopify from './components/Shopify';
import ShopifyOrderDetail from './components/ShopifyOrderDetail';
import ShopifyCustomerDetail from './components/ShopifyCustomerDetail';
import Customers from './components/Customers';
import CustomerDetail from './components/CustomerDetail';
import CustomerOrderDetail from './components/CustomerOrderDetail';
import TripleWhale from './components/TripleWhale';
import ApiKeys from './components/ApiKeys';
import Login from './components/Login';
import ProtectedRoute from './components/ProtectedRoute';
import RoleProtectedRoute from './components/RoleProtectedRoute';
import AiAssistant from './components/AiAssistant';
import './App.css';

// Navigation component
const Navigation = ({ isOpen, onToggle, collapsed, onCollapseToggle }) => {
  const location = useLocation();
  const { user, signOut } = useAuth();
  const isAdmin = user?.role === 'admin';

  // Sidebar structure: top-level items are either a single link OR a
  // collapsible group that contains links. Admin-only items are filtered
  // before rendering so the group itself disappears if empty.
  const navGroups = [
    { type: 'link', path: '/', label: 'Dashboard', icon: Home },
    // Customers is its own top-level destination — the unified profile view is
    // the headline feature, not a sub-tab of any platform.
    { type: 'link', path: '/customers', label: 'Customers', icon: Contact },
    {
      type: 'group',
      key: 'platforms',
      label: 'Platforms',
      icon: Layers,
      items: [
        { path: '/google-maps', label: 'Google Maps', icon: Map },
        { path: '/tiktok',      label: 'TikTok',      icon: Music },
        // Old split pages — replaced by the unified Google Maps page above.
        // { path: '/reviews',  label: 'Reviews',     icon: FileText },
        // { path: '/analysis', label: 'AI Analysis', icon: Brain },
        { path: '/nps',      label: 'NPS Scores',  icon: Award },
        ...(isAdmin ? [{ path: '/nps-ledger', label: 'NPS Ledger', icon: GitCompare }] : []),
        { path: '/calls',    label: 'Avaya Calls', icon: Phone },
        { path: '/shopify',  label: 'Shopify',     icon: ShoppingBag },
        { path: '/triple-whale', label: 'Triple Whale', icon: Target },
      ],
    },
    {
      type: 'group',
      key: 'settings',
      label: 'Settings',
      icon: SettingsIcon,
      items: [
        { path: '/branches', label: 'Branches', icon: MapPin },
        ...(isAdmin ? [
          { path: '/team',     label: 'Team Management', icon: Users },
          { path: '/api-keys', label: 'API Keys',        icon: Key },
          { path: '/cron',     label: 'Cron Monitor',    icon: Activity },
        ] : []),
      ],
    },
  ];

  // A group is "active" when the current route matches one of its items.
  // We seed the expanded state so the active group opens automatically.
  const groupContainsPath = (group, path) =>
    group.type === 'group' && group.items.some(i => i.path === path);

  const [expandedGroups, setExpandedGroups] = useState(() => {
    const initial = {};
    for (const g of navGroups) {
      if (g.type === 'group') {
        initial[g.key] = groupContainsPath(g, location.pathname);
      }
    }
    return initial;
  });

  // If navigation happens (via Link, programmatic, etc) into a group, open it.
  useEffect(() => {
    setExpandedGroups(prev => {
      const next = { ...prev };
      for (const g of navGroups) {
        if (g.type === 'group' && groupContainsPath(g, location.pathname)) {
          next[g.key] = true;
        }
      }
      return next;
    });
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-close sidebar on mobile when route changes
  useEffect(() => {
    if (window.innerWidth <= 1024 && isOpen) {
      onToggle();
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const closeSidebarOnMobile = () => {
    if (window.innerWidth <= 1024 && isOpen) onToggle();
  };

  const toggleGroup = (key) =>
    setExpandedGroups(prev => ({ ...prev, [key]: !prev[key] }));

  const handleLogout = async () => {
    try {
      closeSidebarOnMobile();
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const renderLink = (item) => {
    const Icon = item.icon;
    const isActive = location.pathname === item.path;
    return (
      <Link
        key={item.path}
        to={item.path}
        className={`nav-link ${isActive ? 'active' : ''}`}
        onClick={closeSidebarOnMobile}
        title={item.label}
      >
        <Icon size={20} />
        <span>{item.label}</span>
      </Link>
    );
  };

  return (
    <nav className={`navigation ${isOpen ? 'open' : ''} ${collapsed ? 'collapsed' : ''}`}>
      <button
        type="button"
        className="sidebar-collapse-btn"
        onClick={onCollapseToggle}
        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
      </button>
      <div className="nav-brand">
        <div className="logo-container">
          <img src="/Logo.png" alt="Al Rugaib Logo" className="company-logo" />
        </div>
        <h1 className="gradient-text">Reviews Hub</h1>
        <p className="nav-subtitle">Google Maps Analytics</p>
      </div>

      <div className="nav-links">
        {navGroups.map((g) => {
          if (g.type === 'link') return renderLink(g);
          if (g.items.length === 0) return null; // group hidden for this user
          const GroupIcon = g.icon;
          const isExpanded = !!expandedGroups[g.key];
          const hasActiveChild = groupContainsPath(g, location.pathname);
          return (
            <div
              key={g.key}
              className={`nav-group ${isExpanded ? 'open' : ''} ${hasActiveChild ? 'has-active' : ''}`}
            >
              <button
                type="button"
                className="nav-group-header"
                onClick={() => toggleGroup(g.key)}
                aria-expanded={isExpanded}
                title={g.label}
              >
                <GroupIcon size={20} />
                <span className="nav-group-label">{g.label}</span>
                <ChevronDown
                  size={16}
                  className={`nav-group-chevron ${isExpanded ? 'open' : ''}`}
                />
              </button>
              <AnimatePresence initial={false}>
                {isExpanded && (
                  <motion.div
                    className="nav-group-items"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.18 }}
                  >
                    {g.items.map(renderLink)}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>

      {/* User Profile and Logout - Bottom of Sidebar */}
      <div className="nav-bottom">
        {/* User Profile */}
        <div className="user-profile">
          <div className="user-avatar">
            <User size={16} />
          </div>
          <div className="user-info">
            <span className="user-email">{user?.email}</span>
            <span className="user-status">Logged in</span>
          </div>
        </div>

        {/* Logout Button */}
        <motion.button
          className="logout-btn"
          onClick={handleLogout}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <LogOut size={16} />
          <span>Logout</span>
        </motion.button>
      </div>
    </nav>
  );
};

// App Content Component (authenticated)
const AppContent = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Desktop-only: collapse the sidebar to an icon rail to reclaim page width.
  // Remembered per browser; defaults to expanded so nothing changes until used.
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('sidebarCollapsed') === '1'; } catch { return false; }
  });

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const toggleCollapsed = () => {
    setCollapsed((c) => {
      const next = !c;
      try { localStorage.setItem('sidebarCollapsed', next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  };

  return (
    <div className="app">
      <Navigation
        isOpen={sidebarOpen}
        onToggle={toggleSidebar}
        collapsed={collapsed}
        onCollapseToggle={toggleCollapsed}
      />

      {/* Global AI assistant — floating button top-right, slides in from right */}
      <AiAssistant />

      {/* Backdrop overlay for mobile */}
      {sidebarOpen && (
        <div
          className="sidebar-backdrop"
          onClick={toggleSidebar}
          onTouchEnd={(e) => {
            e.preventDefault();
            toggleSidebar();
          }}
        />
      )}
      
      <main className={`main-content ${sidebarOpen ? 'sidebar-open' : ''} ${collapsed ? 'collapsed' : ''}`}>
        <button 
          className="burger-menu" 
          onClick={toggleSidebar}
          onTouchEnd={(e) => {
            e.preventDefault();
            toggleSidebar();
          }}
          style={{ 
            pointerEvents: 'auto',
            userSelect: 'none',
            WebkitUserSelect: 'none'
          }}
        >
          {sidebarOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
        <Routes>
          <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/dashboard-legacy" element={<ProtectedRoute><DashboardLegacy /></ProtectedRoute>} />
          <Route path="/google-maps" element={<ProtectedRoute><GoogleMaps /></ProtectedRoute>} />
          <Route path="/tiktok" element={<ProtectedRoute><TikTok /></ProtectedRoute>} />
          <Route path="/reviews" element={<ProtectedRoute><Reviews /></ProtectedRoute>} />
          <Route path="/analysis" element={<ProtectedRoute><Analysis /></ProtectedRoute>} />
          <Route path="/branches" element={<ProtectedRoute><Branches /></ProtectedRoute>} />
          <Route path="/nps" element={<ProtectedRoute><NpsLeaderboard /></ProtectedRoute>} />
          <Route path="/nps-ledger" element={<RoleProtectedRoute allowedRoles={['admin']}><NpsLedger /></RoleProtectedRoute>} />
          <Route path="/calls" element={<ProtectedRoute><Calls /></ProtectedRoute>} />
          {/* Agents route MUST come before /calls/:id so "agents" isn't treated as a call id */}
          <Route path="/calls/agents/:extension" element={<ProtectedRoute><AgentProfile /></ProtectedRoute>} />
          <Route path="/calls/:id" element={<ProtectedRoute><CallDetail /></ProtectedRoute>} />
          {/* Shopify — single entry; internal tabs live inside the Shopify component. */}
          {/*  Detail pages stay as separate full-page routes for deep linking. */}
          <Route path="/shopify" element={<ProtectedRoute><Shopify /></ProtectedRoute>} />
          <Route path="/shopify/orders/:id" element={<ProtectedRoute><ShopifyOrderDetail /></ProtectedRoute>} />
          <Route path="/shopify/customers/:id" element={<ProtectedRoute><ShopifyCustomerDetail /></ProtectedRoute>} />
          {/* Customers — Dynamics-backed unified profile. Live order detail
              must be declared BEFORE :id so 'orders' isn't matched as a customer id. */}
          <Route path="/customers" element={<ProtectedRoute><Customers /></ProtectedRoute>} />
          <Route path="/customers/orders/:orderNumber" element={<ProtectedRoute><CustomerOrderDetail /></ProtectedRoute>} />
          <Route path="/customers/:id" element={<ProtectedRoute><CustomerDetail /></ProtectedRoute>} />
          <Route path="/triple-whale" element={<ProtectedRoute><TripleWhale /></ProtectedRoute>} />
          <Route path="/team" element={
            <ProtectedRoute>
              <RoleProtectedRoute allowedRoles={['admin']}>
                <TeamManagement />
              </RoleProtectedRoute>
            </ProtectedRoute>
          } />
          <Route path="/cron" element={
            <ProtectedRoute>
              <RoleProtectedRoute allowedRoles={['admin']}>
                <CronMonitor />
              </RoleProtectedRoute>
            </ProtectedRoute>
          } />
          <Route path="/api-keys" element={
            <ProtectedRoute>
              <RoleProtectedRoute allowedRoles={['admin']}>
                <ApiKeys />
              </RoleProtectedRoute>
            </ProtectedRoute>
          } />
        </Routes>
      </main>
    </div>
  );
};

// Main App component
function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <Router>
          <Routes>
            {/* Login route - completely separate from main app */}
            <Route path="/login" element={<Login />} />
            {/* All other routes use the main app layout */}
            <Route path="/*" element={<AppContent />} />
          </Routes>
        </Router>
      </ToastProvider>
    </AuthProvider>
  );
}

export default App;
